import type { PullRequestReaction, PullRequestReactionContent } from "@afar-research/contracts";
import { SmilePlusIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { newCommandId } from "../../lib/utils";
import { cn } from "../../lib/utils";
import { executePullRequestDiscussion } from "../../pullRequestDiscussionCommand";
import { invalidatePullRequest } from "../../pullRequestInvalidation";
import type { PullRequestSurfaceDescriptor } from "../../pullRequestSurfaceStore";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import {
  applyPendingPullRequestReactions,
  PULL_REQUEST_REACTION_ORDER,
  pullRequestReactionEmoji,
  pullRequestReactionName,
  pullRequestReactionTooltip,
} from "./pullRequestReactions.logic";

/**
 * The reaction pills under a remark, and the picker that adds one, following T3Code. The same
 * bar serves a conversation comment and a review thread's comments: what differs is only which
 * subject the Environment is told about.
 *
 * The add button is revealed by hovering the remark it belongs to, so the parent carries
 * `group/remark`. It stays put once there is something beside it, while the picker is open, and
 * whenever it is focused — a control only a mouse can find is no control.
 */

const PILL_CLASS =
  "inline-flex h-6 shrink-0 items-center gap-1 rounded-full border px-2 text-xs transition-colors";

/** What the host last said, so a press in flight is forgotten the moment real counts land. */
function signature(reactions: ReadonlyArray<PullRequestReaction>): string {
  return reactions
    .map((reaction) => `${reaction.content}:${reaction.count}:${reaction.viewerHasReacted ? 1 : 0}`)
    .join(" ");
}

export function PullRequestReactionBar({
  reactions,
  subjectId,
  descriptor,
  projectRevision,
  resourceVersion,
  canReact,
  className,
}: {
  /** Absent where the Environment has not read them; the bar then says nothing at all. */
  readonly reactions: ReadonlyArray<PullRequestReaction> | undefined;
  /** Null reacts to the pull request itself rather than to one of its comments. */
  readonly subjectId: string | null;
  readonly descriptor: PullRequestSurfaceDescriptor;
  /** The revision the remark was read at, which is what the Environment checks the write against. */
  readonly projectRevision: string | undefined;
  readonly resourceVersion: string;
  /**
   * Whether this reader may react at all. The host is the authority and refuses what it must; a
   * refusal puts the pill back, so this only decides whether to offer the control.
   */
  readonly canReact: boolean;
  readonly className?: string | undefined;
}) {
  const [pending, setPending] = useState<ReadonlyMap<PullRequestReactionContent, boolean>>(
    () => new Map(),
  );
  const [open, setOpen] = useState(false);
  const given = reactions ?? [];
  const hostSignature = signature(given);
  // The host's answer is the truth; a press is only a guess about what it will say.
  useEffect(() => {
    setPending(new Map());
  }, [hostSignature]);

  if (reactions === undefined) return null;

  const offered = canReact && projectRevision !== undefined;
  const shown = applyPendingPullRequestReactions(given, pending);

  const react = (content: PullRequestReactionContent, present: boolean) => {
    if (!offered || projectRevision === undefined) return;
    setPending((current) => new Map(current).set(content, present));
    void executePullRequestDiscussion({
      environmentId: descriptor.environmentId,
      projectId: descriptor.projectId,
      repository: descriptor.repository,
      number: descriptor.number,
      expectedRevision: projectRevision,
      resourceVersion,
      commandId: newCommandId(),
      action: { kind: "reaction", commentId: subjectId, content, present },
    })
      .then((result) => {
        if (result.status === "settled" && result.receipt.status === "accepted") {
          invalidatePullRequest({
            environmentId: descriptor.environmentId,
            projectId: descriptor.projectId,
            repository: descriptor.repository,
            number: descriptor.number,
            partitions: ["summary", "timeline"],
          });
          return;
        }
        // A refusal puts the pill back where the host still has it.
        setPending((current) => {
          const next = new Map(current);
          next.delete(content);
          return next;
        });
      })
      .catch(() => {
        setPending((current) => {
          const next = new Map(current);
          next.delete(content);
          return next;
        });
      });
  };

  if (shown.length === 0 && !offered) return null;

  return (
    <span className={cn("flex flex-wrap items-center gap-1", className)}>
      {shown.map((reaction) => (
        <Tooltip key={reaction.content}>
          <TooltipTrigger
            render={
              <button
                aria-label={`${reaction.viewerHasReacted ? "Remove" : "Add"} ${pullRequestReactionName(reaction.content)} reaction`}
                aria-pressed={reaction.viewerHasReacted}
                className={cn(
                  PILL_CLASS,
                  reaction.viewerHasReacted
                    ? "border-primary/60 bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground hover:bg-accent/60",
                  !offered && "cursor-default opacity-80",
                )}
                disabled={!offered}
                onClick={() => react(reaction.content, !reaction.viewerHasReacted)}
                type="button"
              />
            }
          >
            <span aria-hidden>{pullRequestReactionEmoji(reaction.content)}</span>
            <span className="tabular-nums">{reaction.count}</span>
          </TooltipTrigger>
          <TooltipPopup>{pullRequestReactionTooltip(reaction)}</TooltipPopup>
        </Tooltip>
      ))}
      {offered ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={
              <button
                aria-label="Add a reaction"
                className={cn(
                  PILL_CLASS,
                  "border-border text-muted-foreground opacity-0 hover:bg-accent/60 focus-visible:opacity-100 group-hover/remark:opacity-100",
                  (open || shown.length > 0) && "opacity-100",
                )}
                type="button"
              />
            }
          >
            <SmilePlusIcon aria-hidden className="size-3.5" />
          </PopoverTrigger>
          <PopoverPopup align="start" className="w-auto p-1" side="top">
            <span className="flex items-center gap-0.5">
              {PULL_REQUEST_REACTION_ORDER.map((content) => {
                const held = shown.find((reaction) => reaction.content === content);
                return (
                  <button
                    aria-label={pullRequestReactionName(content)}
                    aria-pressed={held?.viewerHasReacted === true}
                    className={cn(
                      "rounded p-1 text-base hover:bg-accent",
                      held?.viewerHasReacted === true && "bg-primary/10",
                    )}
                    key={content}
                    onClick={() => {
                      react(content, held?.viewerHasReacted !== true);
                      setOpen(false);
                    }}
                    type="button"
                  >
                    <span aria-hidden>{pullRequestReactionEmoji(content)}</span>
                  </button>
                );
              })}
            </span>
          </PopoverPopup>
        </Popover>
      ) : null}
    </span>
  );
}
